import { useNavigate } from 'react-router';
import { useData } from '../../../../contexts/data-context/use-data';
import { LoadingSection } from '../../../../design-system/loading/loading';
import { Stack } from '../../../../design-system/stack/stack';
import { type AccountView } from '../../../../types/entity-types';
import { useEntityModalState } from '../../../../utils/use-entity-modal-state';
import { TransactionDetailsDrawerWrapper } from '../../../transactions/components/transaction-details-drawer/transaction-details-drawer-wrapper';
import { RecentTransactionsTable } from '../../components/tables/recent-transactions-table';

type RecentTransactionsWrapperProps = {
  accounts: AccountView[];
};

const MAX_TRANSACTIONS = 10;

export const RecentTransactionsWrapper = ({ accounts }: RecentTransactionsWrapperProps) => {
  const { data, isLoading } = useData();
  const { mutations: modalMutations, selectedId } = useEntityModalState({
    defaultView: 'transaction',
  });

  const navigate = useNavigate();

  if (isLoading || !data) {
    return <LoadingSection text="Loading recent transactions..." />;
  }

  // Flatten all transactions from all accounts and sort by date (most recent first)
  const allTransactions = accounts
    .flatMap((account) => account.transactions || [])
    .sort((a, b) => new Date(b.fspDate).getTime() - new Date(a.fspDate).getTime());

  // Find the selected transaction from the ID
  const selectedTransaction = selectedId ? allTransactions.find((t) => t.id === selectedId) || null : null;

  const handleSelectTransaction = (transactionId: string) => {
    modalMutations.changeSelectedItem(transactionId);
  };

  return (
    <Stack className="w-full">
      <RecentTransactionsTable
        transactions={allTransactions}
        onSelectRow={handleSelectTransaction}
        showFilter={true}
        className="w-full"
        maxTransactions={MAX_TRANSACTIONS}
        onViewAll={() => {
          navigate('/dashboard/transactions');
        }}
      />
      <TransactionDetailsDrawerWrapper
        selectedTransaction={selectedTransaction}
        transactions={allTransactions}
        categories={data.categories}
        onCloseDrawer={() => {
          modalMutations.resetSearchParams();
        }}
        onChangeSelectedTransaction={modalMutations.changeSelectedItem}
      />
    </Stack>
  );
};
